import { useEffect, useMemo, useRef } from 'react';

import { styled } from '@material-ui/core';

import type { AnyExtension, Editor as TiptapEditor } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import ListItem from '@tiptap/extension-list-item';
import Placeholder from '@tiptap/extension-placeholder';
import { Content, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import type { ImageUploadClient } from '@spotify/confidence-backstage-image-upload';

import { useTiptapPersistence } from '../hooks';
import { Block } from './Block';
import { BlockMenu } from './BlockMenuPlugin';
import { BlockToolbar } from './BlockToolbar';
import { Blockquote } from './Blockquote';
import { Caption } from './Caption';
import { Column, Columns } from './Columns';
import { EditorProvider } from './Context';
import { EditorComments } from './EditorComments';
import { FloatingBlockToolbar } from './FloatingBlockToolbar';
import { Heading } from './Heading';
import { Highlight as HighlightComponent } from './Highlight';
import { Image } from './Image';
import { ImageCard } from './ImageCard';
import { Link, LinkHoverToolbar } from './Link';
import { BulletList, OrderedList } from './List';
import { Paragraph } from './Paragraph';
import { SelectionToolbar } from './SelectionToolbar';
import { SemanticColorMark, SemanticHighlightMark } from './SemanticColor';
import { Tag } from './Tag';
import { type BlockCommand, getAvailableCommands } from './commands';
import { getEditorStyles } from './editorStyles';
import {
  TableCell,
  TableExtension,
  TableHeader,
  TableRow,
  TableToolbar,
} from './table';

const getCodeBlockExtension = (): AnyExtension => {
  // Loading CodeBlock eagerly evaluates @tiptap/extension-code-block-lowlight,
  // which breaks unrelated Jest suites that import the editor package barrel.
  return module.require('./CodeBlock').CodeBlock;
};

export type EditorImageUploadAdapter = {
  imageUploadClient?: ImageUploadClient;
  uploadFile: (file: File) => Promise<string>;
  onUploadError?: (error: Error) => void;
};

export type TipTapEditorProps = {
  readonly?: boolean;
  isBusy?: boolean;
  disableToolbar?: boolean;
  extensions?: AnyExtension[];
  commandResolver?: (editor: TiptapEditor) => BlockCommand[];
  imageUpload?: EditorImageUploadAdapter;
  enableComments?: boolean;
  commentResource?: string;
  placeholder?: string;
  loadingFallback?: JSX.Element | null;
  disableFloatingBlockToolbar?: boolean;
  onCreate?: (editor: TiptapEditor, hasContent: boolean) => void;
  onUpdate?: (editor: TiptapEditor, hasContent: boolean) => void;
  children?: (
    editor: NonNullable<ReturnType<typeof useEditor>>,
  ) => JSX.Element | null;
};

const StyledEditorContent = styled(EditorContent)(({ theme }) =>
  getEditorStyles(theme),
);

export const shouldShowEmptyState = (content?: Content): boolean => {
  if (!content) return true;
  if (typeof content === 'string') return content.trim().length === 0;
  if (Array.isArray(content)) return content.length === 0;
  if (content.type !== 'doc') return false;

  if (!content.content || content.content.length === 0) return true;

  return content.content.every(item => {
    if (item.type !== 'paragraph' && item.type !== 'heading') return false;
    return !item.content || item.content.length === 0;
  });
};

export function createBaseExtensions(options?: {
  imageUpload?: EditorImageUploadAdapter;
  placeholder?: string;
  disableUndoRedo?: boolean;
}): AnyExtension[] {
  const codeBlock = getCodeBlockExtension();
  const imageExtension = options?.imageUpload
    ? Image.configure(options.imageUpload)
    : Image;

  return [
    StarterKit.configure({
      heading: false,
      paragraph: false,
      blockquote: false,
      codeBlock: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      link: false,
      undoRedo: options?.disableUndoRedo ? false : undefined,
    }),
    Bold.extend({ name: 'strong' }),
    Italic.extend({ name: 'em' }),
    Block,
    BulletList,
    Blockquote,
    Caption,
    codeBlock,
    Column,
    Columns,
    Heading,
    HighlightComponent,
    imageExtension,
    ImageCard,
    ListItem,
    OrderedList,
    Paragraph,
    SemanticColorMark,
    SemanticHighlightMark,
    TableCell,
    TableExtension,
    TableHeader,
    TableRow,
    Tag,
    Link.configure({
      openOnClick: true,
    }),
    Placeholder.configure({
      placeholder:
        options?.placeholder ?? 'Write something or press "/" for commands...',
      includeChildren: true,
    }),
  ];
}

export const EditorToolbar = ({
  editor,
  disableFloatingBlockToolbar = false,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  disableFloatingBlockToolbar?: boolean;
}) => (
  <EditorProvider editor={editor}>
    <StyledEditorContent editor={editor} />
    {!disableFloatingBlockToolbar && <FloatingBlockToolbar />}
    <SelectionToolbar />
    <LinkHoverToolbar />
    <TableToolbar />
    <BlockToolbar />
  </EditorProvider>
);

const EditorContentOnly = ({
  editor,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
}) => (
  <EditorProvider editor={editor}>
    <StyledEditorContent editor={editor} />
  </EditorProvider>
);

export const TipTapEditor = ({
  readonly,
  isBusy = false,
  disableToolbar = false,
  extensions = [],
  commandResolver,
  imageUpload,
  enableComments = false,
  commentResource,
  placeholder,
  loadingFallback = null,
  disableFloatingBlockToolbar = false,
  onCreate,
  onUpdate,
  children,
}: TipTapEditorProps) => {
  const isCreatedRef = useRef(false);
  const {
    collaborationExtension,
    initialContent,
    isRealtimeEditing,
    markContentAsPersisted,
    persistContent,
    providerError,
  } = useTiptapPersistence();

  if (providerError) {
    throw providerError;
  }
  const blockMenuExtension = useMemo(
    () =>
      BlockMenu.configure({
        commands: commandResolver ?? getAvailableCommands,
      }),
    [commandResolver],
  );

  const allExtensions = useMemo(
    () => [
      ...createBaseExtensions({
        imageUpload,
        placeholder,
        disableUndoRedo: !!collaborationExtension,
      }),
      ...extensions,
      ...(collaborationExtension ? [collaborationExtension] : []),
      blockMenuExtension,
    ],
    [
      extensions,
      collaborationExtension,
      blockMenuExtension,
      imageUpload,
      placeholder,
    ],
  );

  const editor = useEditor({
    extensions: allExtensions,
    content: initialContent,
    editable: !readonly && !isBusy,
    onCreate: ({ editor: editorInstance }) => {
      const content = editorInstance.getJSON();
      if (!isRealtimeEditing) {
        markContentAsPersisted(content);
      }
      isCreatedRef.current = true;
      const hasContent = !shouldShowEmptyState(content);
      onCreate?.(editorInstance, hasContent);
    },
    onUpdate: ({ editor: editorInstance }) => {
      const content = editorInstance.getJSON();
      const hasContent = !shouldShowEmptyState(content);
      if (!isRealtimeEditing && isCreatedRef.current) {
        persistContent(content);
      }
      onUpdate?.(editorInstance, hasContent);
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readonly && !isBusy);
    }
  }, [editor, readonly, isBusy]);

  if (!editor) {
    return loadingFallback;
  }

  const defaultContent = disableToolbar ? (
    <EditorContentOnly editor={editor} />
  ) : (
    <EditorToolbar
      editor={editor}
      disableFloatingBlockToolbar={disableFloatingBlockToolbar}
    />
  );
  const content = children?.(editor) ?? defaultContent;

  if (enableComments && commentResource) {
    return (
      <EditorComments editor={editor} resource={commentResource}>
        {content}
      </EditorComments>
    );
  }

  return content;
};
